import { useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import {
  getCurrentCompanyAusIqOptionsAsync,
} from "features/company/companySlice";
import { Table } from "~/Common";
import { Box, Heading, Text } from "theme-ui";
import { numberWithCommas } from "utils/helpers";

const mapStateToProps = (state) => ({
  company: state.companies?.currentCompany,
  companies: state.companies,
});

const Options = ({ company, companies }) => {
  const dispatch = useDispatch();
  const prevAsxRef = useRef();

  const { options, optionsStatus } = companies?.currentCompanyExtras?.ausIq ?? {};

  useEffect(() => {
    const asx = company?.asx;
    if (!asx) return;

    const asxChanged = prevAsxRef.current !== asx;

    if (optionsStatus === "idle" || asxChanged) {
      dispatch(getCurrentCompanyAusIqOptionsAsync(asx));
    }

    prevAsxRef.current = asx;
  }, [company?.asx, dispatch, optionsStatus]);

  const columns = [
    {
      key: "security_code",
      title: "Security Code",
      align: "left",
      render: (p) => (
        <Box sx={{ position: "relative", alignItems: "center" }}>
          <Heading as="h3" variant="h5" sx={{ mb: 1 }}>
            {p?.asx_security_code || "-"}
          </Heading>
          <Text as="p" sx={{ fontSize: "10px", textTransform: 'capitalize' }} color="darker">
            {p?.description.toLowerCase() || "-"}
          </Text>
        </Box>
      ),
    },
    {
      key: "exercise_date",
      title: "Expiry Date",
      align: "left",
      render: (p) =>
        <Text as="p" variant="h6">
          {p.exercise_date ? new Date(p.exercise_date).toLocaleDateString() : "-"}
        </Text>
    },
    {
      key: "exercise_price",
      title: "Exercise Price",
      align: "left",
      render: (p) =>
          <Text as="p" variant="h6">
          {p.exercise_price ? `$${p.exercise_price}` : "-"}
        </Text>
    },
    {
      key: "last_price",
      title: "Last Price",
      align: "left",
      render: (p) => (
        <Text as="p" variant="h6" color={p?.exercise_price && p?.last_price > p?.exercise_price ? "green" : p?.last_price < p?.exercise_price ? "red" : "darker"}>
          {p.last_price ? `$${p.last_price}` : "-"}
        </Text>
      ),
    },
    {
      key: "list_unlisted",
      title: "Listed/Unlisted",
      align: "left",
      render: (p) => (
        <Text as="p" variant="h6">{p?.listed_unlisted || "-"}</Text>
      ),
    },
    {
      key: "time_to_expiry_months",
      title: "Time to Expiry (Months)",
      align: "center",
      render: (p) => (
        <Text as="p" variant="h6" color="darker">
          {p?.time_to_expiry_months || "-"}
        </Text>
      ),
    },
    {
      key: "total_number_of_securities",
      title: "Total Number of Securities",
      align: "center",
      render: (p) => (
        <Text as="p" variant="h6" color="darker">
          {numberWithCommas(p?.total_number_of_securities) || "-"}
        </Text>
      ),
    },
  ];

  const mapRowKey = (p) => `${p?.asx_security_code || "unknown"}-${p?.exercise_date || "na"}`;

  const rowProps = () => ({
    sx: {
      textDecoration: "none",
      color: "darker",
      ".table-row__arrow": {
        transform: "translateX(-4px)",
        transition: "transform 0.3s ease-in-out",
      },
      "&:hover .table-row__arrow": {
        transform: "translateX(0)",
        color: "tealDark",
      },
    },
  });

  return (
    <Table
      data={options}
      columns={columns}
      prefix="mining_projects"
      isLoading={optionsStatus === "loading"}
      mapRowKey={mapRowKey}
      rowProps={rowProps}
      my={3}
      minWidth="1200px"
      emptyTemplate={{
        icon: "coin",
        heading: "No Options Data",
        content: (
          <Text variant="h6">
            We couldn’t find any options data for this company.
          </Text>
        ),
      }}
    />
  );
};

export default connect(mapStateToProps)(Options);
